import { ArticleType, ArticleTypeExternal, CheckoutSession, Currency } from '@nemuru-eng/js-api-client';

import { CartSummaryData } from '@/components/modules/_shared/CartSummary/types/CartSummaryData';
import { CartSummaryLine } from '@/components/modules/_shared/CartSummary/types/CartSummaryLine';
import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';
import { ArticleTypeMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/maps/ArticleTypeMap';
import { FormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/FormItem';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';
import { normalizeSpanishLicensePlate } from '@/utils/zodValidation/validateSpanishLicensePlate';

export class CartSummaryMap {
  private static readonly VAT_RATE = 0.21;

  public static isSingleAutomobileCart(cartSummaryData: CartSummaryData): boolean {
    return (
      cartSummaryData.cartSummaryLines.length === 1 &&
      cartSummaryData.cartSummaryLines[0].articleType === ArticleType.AUTOMOBILE
    );
  }

  public static fromCartToCartSummary(cart: CartEntity, showOnlyCompletedItems: boolean = true): CartSummaryData {
    const cartItems = showOnlyCompletedItems ? cart.getCompetedItems() : cart.items;

    return {
      total: cart.total(),
      totalVat: cart.totalVat() ?? 0,
      currency: cart.currency,
      cartSummaryLines: cartItems.map((item) => CartSummaryMap.fromCartItemToCartSummaryLine(item, cart.currency)),
    };
  }

  public static fromCartItemToCartSummaryLine(item: Item<FormItem>, currency: Currency): CartSummaryLine {
    const baseLine: CartSummaryLine = {
      articleType: item.data.type,
      units: item.units,
      description: item.data.description || 'Cart Item',
      totalPrice: { value: item.totalPrice, currency },
    };

    if (item.data.type === ArticleType.AUTOMOBILE) {
      const autoItem = item.data as AutomobileFormItem;

      return {
        ...baseLine,
        identificationNumber: autoItem.identificationNumber,
        plateNumber: autoItem.plateNumber ? normalizeSpanishLicensePlate(autoItem.plateNumber) : autoItem.plateNumber,
        odometer: autoItem.odometer ? parseInt(autoItem.odometer) : undefined,
        isNewVehicle: autoItem.isNewVehicle,
      };
    }

    return baseLine;
  }

  public static fromOrderLineToCartSummaryLine(line: CheckoutSession['cart']['orderLines'][number]): CartSummaryLine {
    const articleType = ArticleTypeMap.toArticleType(line.article.type as ArticleTypeExternal);

    if (line.article.type === ArticleTypeExternal.AUTOMOBILE) {
      return {
        articleType,
        units: line.units,
        description: line.article.description || line.article.name || 'Cart Item',
        totalPrice: amountToValueMoneyMapper(line.article.price),
        plateNumber: line.article.usage?.plateNumber
          ? normalizeSpanishLicensePlate(line.article.usage.plateNumber)
          : line.article.usage?.plateNumber,
        identificationNumber: line.article.specs?.identificationNumber,
        odometer: line.article.usage?.odometer,
        isNewVehicle: line.article.isNew,
      };
    }

    return {
      articleType,
      units: line.units,
      description: line.article.description || line.article.name || 'Cart Item',
      totalPrice: amountToValueMoneyMapper(line.article.price),
    };
  }

  public static fromCheckoutSessionToCartSummaryData(checkoutSession: CheckoutSession): CartSummaryData {
    const total = checkoutSession.cart.orderLines.reduce(
      (acc, line) => acc + line.article.price.amount * line.units,
      0,
    );

    const totalDiscount = checkoutSession.cart.orderLines.reduce(
      (acc, line) => acc + (line.totalDiscount?.amount ?? 0),
      0,
    );

    const totalMinusDiscount = total - totalDiscount;

    if (totalMinusDiscount !== checkoutSession.cart.totalPriceWithTax.amount) {
      console.log('totalWithoutDiscount does not match the totalPriceWithTax');
    }

    const subTotal = total / (1 + CartSummaryMap.VAT_RATE);
    const totalVat = total - subTotal;

    return {
      total: total,
      totalVat: totalVat,
      totalDiscount: totalDiscount,
      currency: checkoutSession.totalPrice.currency,
      cartSummaryLines: checkoutSession.cart.orderLines.map((line) =>
        CartSummaryMap.fromOrderLineToCartSummaryLine(line),
      ),
    };
  }
}
